import React, { useState } from "react"
import styled from "styled-components"
import Img from "gatsby-image"
import { graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import {
  FiArrowRight,
  FiArrowDown,
  FiCpu,
  FiCode,
  FiWifi,
  FiCloud,
  FiActivity,
  FiShield,
  FiThermometer,
  FiZap,
  FiSettings,
  FiMonitor,
} from "react-icons/fi"
import Link from "../../components/Link"
import PageMetadata from "../../components/PageMetadata"
import { translateMessageId } from "../../utils/translations"

const Page = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.text};
  h1,
  h2,
  h3 {
    font-family: inherit;
    font-style: normal;
  }
  h2 {
    font-size: clamp(1.9rem, 3vw, 2.75rem);
    font-weight: 500;
    line-height: 1.15;
    letter-spacing: -0.035em;
    margin: 0 0 1.25rem;
  }
  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.025em;
    margin: 0 0 0.9rem;
  }
  a:focus-visible,
  button:focus-visible,
  summary:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 5px;
  }
`

const Container = styled.div`
  width: min(100%, 1200px);
  margin: 0 auto;
  padding: 0 2.5rem;
  min-width: 0;
  @media (max-width: 600px) {
    padding: 0 1.25rem;
  }
`

const Hero = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  color: #fff;
  background: #0b1b2a;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      rgba(4, 14, 24, 0.78) 0%,
      rgba(4, 14, 24, 0.55) 43%,
      rgba(4, 14, 24, 0.05) 80%
    );
  }
  a:focus-visible {
    outline-color: #fff;
  }
  @media (max-width: 750px) {
    display: flex;
    flex-direction: column;
    &::after {
      display: none;
    }
  }
`

const HeroImage = styled(Img)`
  position: absolute !important;
  inset: 0;
  z-index: -2;
  width: 100%;
  height: 100%;
  @media (max-width: 750px) {
    position: relative !important;
    inset: auto;
    z-index: auto;
    order: 2;
    height: clamp(250px, 48vw, 340px);
  }
`

const HeroContent = styled(Container)`
  min-height: 610px;
  padding-top: 4.5rem;
  padding-bottom: 4.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  h1 {
    max-width: 670px;
    color: #fff;
    font-size: clamp(2.75rem, 5.1vw, 4.5rem);
    font-weight: 500;
    line-height: 1.07;
    letter-spacing: -0.045em;
    margin: 0 0 1.75rem;
    text-wrap: balance;
  }
  > p {
    max-width: 500px;
    color: #e0e6ec;
    font-size: 1.1rem;
    line-height: 1.75;
    margin: 0 0 2rem;
  }
  @media (max-width: 750px) {
    order: 1;
    min-height: auto;
    padding-top: 3rem;
    padding-bottom: 3rem;
    h1 {
      font-size: clamp(2.5rem, 8.8vw, 3.75rem);
    }
    > p {
      font-size: 1rem;
    }
  }
`

const Kicker = styled.p`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin: 0 0 1.25rem;
  color: ${({ theme }) => theme.colors.primary};
`

const HeroKicker = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 1.75rem;
  &::before {
    content: "";
    flex-shrink: 0;
    width: 2rem;
    height: 2px;
    background: #6d9dff;
  }
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.25rem 2rem;
`

const PrimaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1rem 1.25rem;
  border: 1px solid #fff;
  background: #fff;
  color: #102030 !important;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    background: #dce7ff;
    border-color: #dce7ff;
  }
  svg {
    flex-shrink: 0;
  }
`

const SecondaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: #fff !important;
  font-size: 0.95rem;
  text-decoration: none;
  padding: 0.75rem 0;
  border-bottom: 1px solid #8896a5;
  &:hover {
    border-color: #fff;
  }
`

const SectionNav = styled.nav`
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  background: ${({ theme }) => theme.colors.ednBackground};
  ${Container} {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.95rem;
    padding: 1.4rem 1.5rem;
    border-left: 1px solid ${({ theme }) => theme.colors.lightBorder};
    text-decoration: none;
  }
  a:last-child {
    border-right: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  a:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.background};
  }
  svg {
    flex-shrink: 0;
  }
  @media (max-width: 600px) {
    ${Container} {
      grid-template-columns: 1fr;
    }
    a {
      padding: 1rem 0;
      border-left: 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
    }
    a:last-child {
      border-right: 0;
      border-bottom: 0;
    }
  }
`

const Section = styled.section`
  padding: 5rem 0;
  scroll-margin-top: 6rem;
  @media (max-width: 600px) {
    padding: 3rem 0;
  }
`

const SectionIntro = styled.div`
  max-width: 770px;
  margin-bottom: 2.75rem;
  > p:last-child {
    max-width: 680px;
    font-size: 1.1rem;
    line-height: 1.75;
    color: ${({ theme }) => theme.colors.text200};
    margin: 0;
  }
`

const Method = styled(Section)`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-block: 1px solid ${({ theme }) => theme.colors.lightBorder};
`

const Steps = styled.ol`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 2rem;
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    margin: 0;
    padding: 1.5rem 0 0;
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  li::before {
    display: none;
  }
  span {
    display: block;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 0.8rem;
    letter-spacing: 0.08em;
    margin-bottom: 1.75rem;
  }
  h3 {
    font-size: 1.15rem;
  }
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.95rem;
    line-height: 1.75;
  }
  @media (max-width: 950px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 550px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    span {
      margin-bottom: 0.75rem;
    }
  }
`

const Contact = styled(Section)`
  color: #fff;
  background: #0b1b2a;
  ${Container} {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 3rem;
  }
  ${Kicker} {
    color: #b4cae7;
  }
  h2 {
    color: #fff;
    max-width: 640px;
  }
  p:last-child {
    color: #d9e2eb;
    max-width: 670px;
    line-height: 1.75;
    font-size: 1.05rem;
    margin: 0;
  }
  a:focus-visible {
    outline-color: #fff;
  }
  @media (max-width: 950px) {
    ${Container} {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    a {
      justify-self: start;
    }
  }
`

const Related = styled.aside`
  margin-top: 4rem;
  padding-top: 2.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  h2 {
    font-size: 1.1rem;
    letter-spacing: 0;
  }
  > div {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem 2.5rem;
  }
  a {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    font-size: 0.95rem;
    padding: 0.4rem 0;
  }
  a:hover {
    text-decoration: underline;
  }
`

const FAQ = styled.div`
  display: grid;
  grid-template-columns: 0.85fr 1.4fr;
  gap: 4rem;
  padding-top: 4rem;
  margin-top: 4rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  h2 {
    font-size: clamp(1.75rem, 2.5vw, 2.25rem);
  }
  details {
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  summary {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 1.35rem 0;
    font-size: 1.05rem;
    line-height: 1.4;
    list-style: none;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  summary::after {
    content: "+";
    font-size: 1.4rem;
    color: ${({ theme }) => theme.colors.primary};
    flex-shrink: 0;
  }
  details[open] summary::after {
    content: "−";
  }
  summary:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  details p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.95rem;
    line-height: 1.8;
    margin: 0;
    padding: 0 2rem 1.5rem 0;
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding-top: 3rem;
    margin-top: 3rem;
  }
`

const FAQSection = styled(Section)`
  ${FAQ} {
    margin: 0;
    padding: 0;
    border-top: 0;
  }
`

const Capabilities = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2.75rem 2rem;
  @media (max-width: 950px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`

const Capability = styled.article`
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  padding-top: 1.75rem;
  > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }
  svg {
    width: 1.8rem;
    height: 1.8rem;
    color: ${({ theme }) => theme.colors.primary};
  }
  span {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.75rem;
    letter-spacing: 0.1em;
  }
  p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 1rem;
    line-height: 1.75;
    margin: 0;
  }
`

const Lifecycle = styled(Section)`
  background: #0b1b2a;
  color: #fff;
  h2,
  h3 {
    color: #fff;
  }
  ${Kicker} {
    color: #a9c6ed;
  }
  ${SectionIntro} > p:last-child {
    color: #c2cedb;
  }
  button:focus-visible {
    outline-color: #9cbfff;
    outline-offset: -5px;
  }
`

const StageSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border: 1px solid #3a4c60;
  button {
    appearance: none;
    background: transparent;
    color: #b9c9dc;
    border: 0;
    border-right: 1px solid #3a4c60;
    border-bottom: 3px solid transparent;
    padding: 1.4rem 1.25rem;
    font: inherit;
    text-align: left;
    cursor: pointer;
    min-width: 0;
  }
  button:last-child {
    border-right: 0;
  }
  button:hover {
    background: #172e44;
    color: #fff;
  }
  button[aria-pressed="true"] {
    background: #1b334c;
    color: #fff;
    border-bottom-color: #8db7ff;
  }
  span {
    display: block;
    font-size: 0.7rem;
    letter-spacing: 0.1em;
    color: #a9c6ed;
    margin-bottom: 0.85rem;
  }
  strong {
    font-size: 1rem;
    font-weight: 500;
  }
  svg {
    width: 1.3rem;
    height: 1.3rem;
    display: block;
    margin-bottom: 1.2rem;
  }
  @media (max-width: 700px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    button {
      padding: 1rem;
      border-bottom: 1px solid #3a4c60;
      border-left: 3px solid transparent;
    }
    button:nth-child(even) {
      border-right: 0;
    }
    button:nth-last-child(-n + 2) {
      border-bottom: 0;
    }
    button[aria-pressed="true"] {
      border-left-color: #8db7ff;
      border-bottom-color: #3a4c60;
    }
    svg {
      display: inline-block;
      width: 1rem;
      height: 1rem;
      margin: 0 0.5rem 0 0;
      vertical-align: middle;
    }
    span {
      margin-bottom: 0.5rem;
    }
  }
`

const StagePanel = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 3rem;
  padding: 2.5rem;
  background: #102438;
  border: 1px solid #3a4c60;
  border-top: 0;
  min-height: 245px;
  &[hidden] {
    display: none;
  }
  h3 {
    font-size: 1.65rem;
    max-width: 430px;
  }
  p {
    font-size: 1rem;
    line-height: 1.75;
    color: #c2cedb;
    margin: 0;
  }
  > div:last-child {
    border-left: 1px solid #3a4c60;
    padding-left: 2.5rem;
  }
  h4 {
    font-size: 0.7rem;
    color: #a9c6ed;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    font-weight: 600;
    margin: 0 0 1rem;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 1.5rem;
    min-height: 0;
    > div:last-child {
      border-left: 0;
      border-top: 1px solid #3a4c60;
      padding: 1.5rem 0 0;
    }
  }
`

const LifecycleNote = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: #b9c9dc;
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 1.5rem 0 0;
  svg {
    flex-shrink: 0;
    margin-top: 0.2rem;
    color: #a9c6ed;
  }
`

const UseCases = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;
  @media (max-width: 850px) {
    grid-template-columns: 1fr;
  }
`
const UseCase = styled.article`
  padding: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.lightBorder};
  background: ${({ theme }) => theme.colors.ednBackground};
  display: flex;
  flex-direction: column;
  > svg {
    color: ${({ theme }) => theme.colors.primary};
    width: 1.75rem;
    height: 1.75rem;
    margin-bottom: 1.75rem;
  }
  > p {
    margin: 0 0 1.75rem;
    color: ${({ theme }) => theme.colors.text200};
    line-height: 1.75;
    font-size: 0.95rem;
  }
  > div {
    margin-top: auto;
    padding-top: 1.25rem;
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  h4 {
    font-size: 0.7rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text200};
    margin: 0 0 0.75rem;
  }
  > div p {
    font-size: 0.9rem;
    line-height: 1.65;
    color: ${({ theme }) => theme.colors.primary};
    margin: 0;
  }
  @media (max-width: 400px) {
    padding: 1.5rem;
  }
`
const Technology = styled.aside`
  margin-top: 3.5rem;
  padding-top: 2.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  > h2 {
    font-size: 1.3rem;
  }
  > p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.95rem;
    line-height: 1.7;
    max-width: 720px;
    margin-bottom: 2rem;
  }
  > div {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 2rem;
  }
  h3 {
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
  }
  ul {
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0;
    margin: 0;
  }
  li {
    border: 1px solid ${({ theme }) => theme.colors.lightBorder};
    padding: 0.4rem 0.65rem;
    font-size: 0.8rem;
    margin: 0;
  }
  li::before {
    display: none;
  }
  @media (max-width: 800px) {
    > div {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
  }
`
const capabilities = [
  { id: "hardware", icon: FiCpu },
  { id: "firmware", icon: FiCode },
  { id: "edge", icon: FiWifi },
  { id: "platforms", icon: FiCloud },
  { id: "analytics", icon: FiActivity },
  { id: "security", icon: FiShield },
]
const stages = [
  { id: "device", icon: FiCpu },
  { id: "edge", icon: FiWifi },
  { id: "platform", icon: FiCloud },
  { id: "operation", icon: FiMonitor },
]
const cases = [
  { id: "industrial", icon: FiSettings },
  { id: "energy", icon: FiZap },
  { id: "conditions", icon: FiThermometer },
]
const steps = ["discover", "prototype", "pilot", "scale"]
const technologies = [
  { id: "devices", items: ["Arduino", "Raspberry Pi", "NVIDIA Jetson"] },
  { id: "software", items: ["Python", "C++", "MQTT", "HTTPS"] },
  { id: "platforms", items: ["AWS IoT Core", "AWS IoT Greengrass", "APIs"] },
]
const related = [
  { id: "architecture", to: "/arquitectura/detalle/" },
  { id: "software", to: "/desarrollo/" },
  { id: "ai", to: "/ai-service/" },
  { id: "security", to: "/devsecops/" },
]

const IoTPage = ({ data }) => {
  const intl = useIntl()
  const t = (id) => translateMessageId(`iot-service-${id}`, intl)
  const [activeStage, setActiveStage] = useState("device")

  return (
    <Page>
      <PageMetadata
        title={t("meta-title")}
        description={t("meta-description")}
        image={data.hero.childImageSharp.fixed.src}
      />
      <Hero>
        <HeroImage
          fluid={data.hero.childImageSharp.fluid}
          alt={t("hero-alt")}
          loading="eager"
          imgStyle={{ objectPosition: "74% 20%" }}
        />
        <HeroContent>
          <HeroKicker>{t("eyebrow")}</HeroKicker>
          <h1>{t("title")}</h1>
          <p>{t("intro")}</p>
          <Actions>
            <PrimaryLink to="/#contacto">
              {t("contact")} <FiArrowRight aria-hidden="true" />
            </PrimaryLink>
            <SecondaryLink to="#arquitectura">
              {t("explore")} <FiArrowDown aria-hidden="true" />
            </SecondaryLink>
          </Actions>
        </HeroContent>
      </Hero>
      <SectionNav aria-label={t("navigation")}>
        <Container>
          <Link to="#soluciones">
            {t("nav-services")} <FiArrowDown aria-hidden="true" />
          </Link>
          <Link to="#arquitectura">
            {t("nav-architecture")} <FiArrowDown aria-hidden="true" />
          </Link>
          <Link to="#casos">
            {t("nav-cases")} <FiArrowDown aria-hidden="true" />
          </Link>
        </Container>
      </SectionNav>
      <Section id="soluciones" aria-labelledby="iot-services-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("services-eyebrow")}</Kicker>
            <h2 id="iot-services-title">{t("services-title")}</h2>
            <p>{t("services-intro")}</p>
          </SectionIntro>
          <Capabilities>
            {capabilities.map(({ id, icon: Icon }, index) => (
              <Capability key={id}>
                <div>
                  <Icon aria-hidden="true" />
                  <span aria-hidden="true">0{index + 1}</span>
                </div>
                <h3>{t(`${id}-title`)}</h3>
                <p>{t(`${id}-description`)}</p>
              </Capability>
            ))}
          </Capabilities>
        </Container>
      </Section>
      <Lifecycle id="arquitectura" aria-labelledby="iot-architecture-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("architecture-eyebrow")}</Kicker>
            <h2 id="iot-architecture-title">{t("architecture-title")}</h2>
            <p>{t("architecture-intro")}</p>
          </SectionIntro>
          <StageSelector role="group" aria-label={t("stage-selector")}>
            {stages.map(({ id, icon: Icon }, index) => (
              <button
                type="button"
                key={id}
                aria-pressed={activeStage === id}
                aria-controls={`iot-stage-${id}`}
                onClick={() => setActiveStage(id)}
              >
                <span aria-hidden="true">0{index + 1}</span>
                <Icon aria-hidden="true" />
                <strong>{t(`stage-${id}-name`)}</strong>
              </button>
            ))}
          </StageSelector>
          {stages.map(({ id }) => (
            <StagePanel
              key={id}
              id={`iot-stage-${id}`}
              hidden={activeStage !== id}
              role="region"
              aria-labelledby={`iot-stage-${id}-title`}
            >
              <div>
                <h3 id={`iot-stage-${id}-title`}>{t(`stage-${id}-title`)}</h3>
                <p>{t(`stage-${id}-description`)}</p>
              </div>
              <div>
                <h4>{t("stage-example")}</h4>
                <p>{t(`stage-${id}-example`)}</p>
              </div>
            </StagePanel>
          ))}
          <LifecycleNote>
            <FiShield aria-hidden="true" />
            {t("architecture-note")}
          </LifecycleNote>
        </Container>
      </Lifecycle>
      <Section id="casos" aria-labelledby="iot-cases-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("cases-eyebrow")}</Kicker>
            <h2 id="iot-cases-title">{t("cases-title")}</h2>
            <p>{t("cases-intro")}</p>
          </SectionIntro>
          <UseCases>
            {cases.map(({ id, icon: Icon }) => (
              <UseCase key={id}>
                <Icon aria-hidden="true" />
                <h3>{t(`case-${id}-title`)}</h3>
                <p>{t(`case-${id}-description`)}</p>
                <div>
                  <h4>{t("case-signals")}</h4>
                  <p>{t(`case-${id}-signals`)}</p>
                </div>
              </UseCase>
            ))}
          </UseCases>
        </Container>
      </Section>
      <Method id="metodo" aria-labelledby="iot-method-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("method-eyebrow")}</Kicker>
            <h2 id="iot-method-title">{t("method-title")}</h2>
            <p>{t("method-intro")}</p>
          </SectionIntro>
          <Steps>
            {steps.map((id, index) => (
              <li key={id}>
                <span aria-hidden="true">0{index + 1}</span>
                <h3>{t(`method-${id}-title`)}</h3>
                <p>{t(`method-${id}-description`)}</p>
              </li>
            ))}
          </Steps>
          <Technology aria-labelledby="iot-technology-title">
            <h2 id="iot-technology-title">{t("technology-title")}</h2>
            <p>{t("technology-description")}</p>
            <div>
              {technologies.map(({ id, items }) => (
                <div key={id}>
                  <h3>{t(`technology-${id}`)}</h3>
                  <ul>
                    {items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Technology>
          <Related aria-labelledby="iot-related-title">
            <h2 id="iot-related-title">{t("related-title")}</h2>
            <div>
              {related.map(({ id, to }) => (
                <Link key={id} to={to}>
                  {t(`related-${id}`)} <FiArrowRight aria-hidden="true" />
                </Link>
              ))}
            </div>
          </Related>
        </Container>
      </Method>
      <FAQSection aria-labelledby="iot-faq-title">
        <Container>
          <FAQ>
            <div>
              <Kicker>{t("faq-eyebrow")}</Kicker>
              <h2 id="iot-faq-title">{t("faq-title")}</h2>
            </div>
            <div>
              {["existing", "offline", "ai"].map((id) => (
                <details key={id}>
                  <summary>{t(`faq-${id}-question`)}</summary>
                  <p>{t(`faq-${id}-answer`)}</p>
                </details>
              ))}
            </div>
          </FAQ>
        </Container>
      </FAQSection>
      <Contact aria-labelledby="iot-contact-title">
        <Container>
          <div>
            <Kicker>{t("cta-eyebrow")}</Kicker>
            <h2 id="iot-contact-title">{t("cta-title")}</h2>
            <p>{t("cta-description")}</p>
          </div>
          <PrimaryLink to="/#contacto">
            {t("contact")} <FiArrowRight aria-hidden="true" />
          </PrimaryLink>
        </Container>
      </Contact>
    </Page>
  )
}

export default IoTPage

export const query = graphql`
  query IoTServicePageQuery {
    hero: file(relativePath: { eq: "iot/connected-industry-v1.png" }) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 85) {
          ...GatsbyImageSharpFluid_withWebp
        }
        fixed(width: 1200) {
          src
        }
      }
    }
  }
`
